import Big from "big.js";
import sumBy from "lodash/sumBy";

import {
  BillingDocumentIntegrationStatus,
  BillingDocumentStatus,
} from "constants/billingDocuments";
import { BillingLedgerEntryStatus } from "constants/billingLedgerEntries";

import { formatDollarsFromCents } from "lib";

type BillingDocumentStatusFields = Pick<
  BillingDocument,
  "integrationStatus" | "status"
>;

type LedgerEntriesByDocumentId = Record<
  string,
  { ledgerEntry: Pick<BillingLedgerEntry, "status"> }[]
>;

// Label for a document in a list or dropdown. The label shows whose
// document it is and its outstanding balance. This lets a user tell split
// candidates apart.
export const formatBillingDocumentLabel = (
  billingDocument: Pick<BillingDocument, "type" | "number">,
  issuerBusiness: Pick<Business, "name"> | null | undefined,
  owingDollars: number | null | undefined,
): string => {
  const businessName = issuerBusiness?.name || "Unknown business";
  const owingDisplay = formatDollarsFromCents(
    owingDollars == null ? null : Math.round(owingDollars * 100),
    { format: true },
  );
  return (
    `${billingDocument.type} ${billingDocument.number} - ${businessName}` +
    ` (${owingDisplay} outstanding)`
  );
};

export const billingDocumentAllowsReversal = (
  billingDocument: Pick<BillingDocument, "integrationStatus">,
): boolean =>
  [
    BillingDocumentIntegrationStatus.AUTHORISED,
    BillingDocumentIntegrationStatus.PAID,
  ].includes(billingDocument.integrationStatus);

export const billingDocumentIntegrationIsActive = (
  billingDocument: Pick<BillingDocument, "integrationStatus">,
): boolean =>
  [
    BillingDocumentIntegrationStatus.DRAFT,
    BillingDocumentIntegrationStatus.SUBMITTED,
    BillingDocumentIntegrationStatus.PAID,
    BillingDocumentIntegrationStatus.AUTHORISED,
  ].includes(billingDocument.integrationStatus);

export const expectingDeleteOnAcceptChanges = (
  billingDocument: Pick<BillingDocument, "hasFinancialChanges" | "id">,
  ledgerEntriesByDocumentId: LedgerEntriesByDocumentId,
): boolean => {
  if (!billingDocument.hasFinancialChanges) {
    // shortcut as there are no financial changes to accept.
    return false;
  }

  // check if accepting change will delete this document,
  // don't allow if document has an active integration status
  const ledgerEntries = ledgerEntriesByDocumentId[billingDocument.id] || [];
  const temporaryLedgerEntries = ledgerEntries.filter(
    ({ ledgerEntry }) =>
      ledgerEntry.status === BillingLedgerEntryStatus.TEMPORARY,
  );
  const hasTemporaryLedgerEntries = temporaryLedgerEntries.length > 0;
  return !hasTemporaryLedgerEntries;
};

export const expectingDeleteWithActiveIntegrationOnAcceptChanges = (
  billingDocument: Pick<
    BillingDocument,
    "hasFinancialChanges" | "id" | "integrationStatus"
  >,
  ledgerEntriesByDocumentId: LedgerEntriesByDocumentId,
): boolean => {
  const isExpectingDeleteOnAcceptChanges = expectingDeleteOnAcceptChanges(
    billingDocument,
    ledgerEntriesByDocumentId,
  );
  const hasActiveIntegration =
    billingDocumentIntegrationIsActive(billingDocument);
  return isExpectingDeleteOnAcceptChanges && hasActiveIntegration;
};

export const expectingDeleteWithoutActiveIntegrationOnAcceptChanges = (
  billingDocument: Pick<
    BillingDocument,
    "hasFinancialChanges" | "id" | "integrationStatus"
  >,
  ledgerEntriesByDocumentId: LedgerEntriesByDocumentId,
): boolean => {
  const isExpectingDeleteOnAcceptChanges = expectingDeleteOnAcceptChanges(
    billingDocument,
    ledgerEntriesByDocumentId,
  );
  const hasActiveIntegration =
    billingDocumentIntegrationIsActive(billingDocument);
  return isExpectingDeleteOnAcceptChanges && !hasActiveIntegration;
};

export const integratedBillingDocumentDescription = (
  billingDocument: Pick<
    BillingDocument,
    "type" | "invoiceNumber" | "integrationStatus"
  >,
): string =>
  `${billingDocument.type} ${billingDocument.invoiceNumber} (${billingDocument.integrationStatus})`;

export const billingDocumentIsPaid = (
  billingDocument: BillingDocumentStatusFields &
    Pick<BillingDocument, "totalAmountDollars">,
  payments: Pick<Payment, "totalAmountCents">[],
): boolean => {
  const { status, integrationStatus } = billingDocument;

  // Check status-based payment indicators
  const statusPaid =
    status === BillingDocumentStatus.PAID ||
    integrationStatus === BillingDocumentIntegrationStatus.PAID;

  // Check if total payments equal the document amount
  // sumBy gives undefined/null when every total is missing, which Big rejects.
  const totalPaymentsCents = sumBy(payments, "totalAmountCents") || 0;
  const documentAmountCents = Big(billingDocument.totalAmountDollars).times(
    100,
  );
  const paymentsEqualAmount = documentAmountCents.lte(totalPaymentsCents);

  return statusPaid || paymentsEqualAmount;
};

export const billingDocumentIsApproved = (
  billingDocument: Pick<BillingDocument, "status">,
): boolean => {
  const { status } = billingDocument;
  return [
    BillingDocumentStatus.APPROVED,
    BillingDocumentStatus.SENT,
    BillingDocumentStatus.PAID,
    BillingDocumentStatus.NEEDS_ATTENTION, // This is here for now...
    BillingDocumentStatus.EXPORTED,
    BillingDocumentStatus.EXPORTING,
    BillingDocumentStatus.EXPORTED_WITH_WARNING,
    BillingDocumentStatus.EXPORT_ERROR,
  ].includes(status);
};
